import React from 'react'
import {Trans} from 'react-i18next'
import {useParams, useHistory} from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {Container, Grid, Typography, useTheme, Box} from '@mui/material'
import {getProductLineColors} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import PageLoader from '@progyny/components/PageLoader'
import {useConsultationsProgramDetailQuery} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import ConsultationOverlayHeader from '@progyny/pages/Consultations/components/ConsultationOverlayHeader'
import LearnMoreSlider from '@progyny/pages/Consultations/components/LearnMoreSlider'

interface ProgramInfoParams {
  program: string
  sessionid?: string
}

const useStyles = makeStyles<{colors?: any}>()((theme, {colors}) => ({
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '32px',
    cursor: 'pointer',
    color: '#FFFFFF',
    textDecoration: 'none',
    '&:hover': {
      opacity: 0.8,
    },
  },
  backText: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    color: '#FFFFFF',
  },
}))

const ProgramInfo: React.ComponentType = () => {
  const {program: programSlug, sessionid} = useParams<ProgramInfoParams>()
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {classes} = useStyles({colors})
  const theme = useTheme()
  const history = useHistory()

  const {data, loading} = useConsultationsProgramDetailQuery({
    variables: {slug: programSlug},
    skip: !programSlug,
  })

  const program = data?.consultations?.program

  const handleBackClick = () => {
    const referrer = (history.location.state as any)?.referrer
    if (referrer) {
      history.push(referrer)
    } else if (window.history.length > 1) {
      history.goBack()
    } else {
      history.push('/consultations')
    }
  }

  // Map actual program sessions to slider format
  const sessions =
    program?.sessions?.map((session) => ({
      sessionIndex: String(session?.sessionNumber || ''),
      timeframe: session?.subtitle || '',
      title: session?.title || '',
      description: session?.sessionDetail || session?.description || '',
    })) || []

  if (loading) {
    return (
      <Box minHeight={140}>
        <PageLoader position={'center'} />
      </Box>
    )
  }

  if (!program) {
    return null
  }

  return (
    <Container maxWidth={'xl'}>
      <ConsultationOverlayHeader
        title={program.title}
        description={program.description}
        colors={colors}
        followingContentMode='contentOnTopWide'
      />

      <Grid container>
        {/* Back Button */}
        <Box
          className={classes.backLink}
          onClick={handleBackClick}
          mb={theme.spacing(12)}
        >
          <ArrowBackIcon sx={{fontSize: '20px', color: '#FFFFFF'}} />
          <Typography className={classes.backText}>
            <Trans i18nKey='consultations_onboarding_back_to_overview' />
          </Typography>
        </Box>

        <LearnMoreSlider
          sessions={sessions}
          colors={colors}
          initialSessionId={sessionid}
        />
      </Grid>
    </Container>
  )
}

export default ProgramInfo
